import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import { ProviderForm, emptyProviderFormState, toError } from '@/modules/ai/shared';
import type { ProviderFormResult } from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';

/**
 * The blank form.
 *
 * A module-level constant, not a call in render: the form reads it once on
 * mount, and a fresh object per render would be a new initial value every
 * time.
 */
const EMPTY_FORM = emptyProviderFormState();

/**
 * Creating a platform provider.
 *
 * The provider and its credential are TWO calls, because the contract has two
 * endpoints: `POST /ai/platform/providers` creates the provider, and
 * `POST /ai/platform/providers/{id}/credential` sets its key. The form asks
 * for both at once because an operator thinks of them as one thing; the
 * console does the two-step for them.
 *
 * If the credential call fails after the provider was created, the operator is
 * told — a provider with no key is a real, visible state (its status says "Not
 * configured") and not something to paper over.
 */
export function AiProviderCreatePage(): ReactNode {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  const handleSubmit = async (result: ProviderFormResult): Promise<void> => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const provider = await aiApi.createPlatformProvider(result.provider);

      if (result.apiKey !== null) {
        await aiApi.rotatePlatformCredential(provider.id, { apiKey: result.apiKey });
      }

      void navigate({
        to: '/platform/ai/providers/$providerId',
        params: { providerId: provider.id },
      });
    } catch (cause) {
      setSubmitError(toError(cause));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppDetailLayout
      headerVariant="accent"
      title="Add AI provider"
      subtitle="A provider the platform can call on a tenant's behalf."
      breadcrumbs={[
        { label: 'AI console', href: '/platform/ai' },
        { label: 'Add provider' },
      ]}
      sections={[
        {
          id: 'provider',
          label: 'Provider',
          content: (
            <ProviderForm
              initialValues={EMPTY_FORM}
              submitLabel="Create provider"
              isSubmitting={isSubmitting}
              submitError={submitError}
              credentialLabel="API credential"
              credentialHint="Write-only. It is sent once, never shown again, and never returned by the API."
              onSubmit={(result) => {
                void handleSubmit(result);
              }}
            />
          ),
        },
      ]}
    />
  );
}
